import { emptyValue, expectedValueType, resolveEditor } from './utils';
export function createFilterRow(idFactory) {
    return {
        id: idFactory(),
        parameter: null,
        comparator: null,
        value: null
    };
}
export function createFilterGroup(idFactory) {
    return {
        id: idFactory(),
        rows: [createFilterRow(idFactory)]
    };
}
export function ensureFilterRowId(row, idFactory) {
    return row.id ? row : { ...row, id: idFactory() };
}
export function ensureFilterGroupId(group, idFactory) {
    const rows = group.rows.map((row) => ensureFilterRowId(row, idFactory));
    const hasAllRowIds = rows.every((row, index) => row === group.rows[index]);
    if (group.id && hasAllRowIds) {
        return group;
    }
    return {
        ...group,
        id: group.id ?? idFactory(),
        rows
    };
}
export function normalizeFilterGroups(value, idFactory) {
    return value.map((group) => ensureFilterGroupId(group, idFactory));
}
export function hasMissingFilterIds(value) {
    return value.some((group) => !group.id || group.rows.some((row) => !row.id));
}
export function replaceFilterRow(groups, groupIndex, rowIndex, nextRow, idFactory) {
    return groups.map((group, currentGroupIndex) => {
        if (currentGroupIndex !== groupIndex) {
            return group;
        }
        return {
            ...group,
            rows: group.rows.map((row, currentRowIndex) => currentRowIndex === rowIndex
                ? {
                    ...nextRow,
                    id: nextRow.id ?? row.id ?? idFactory()
                }
                : row)
        };
    });
}
export function buildRowForParameterChange(context, descriptor) {
    const comparator = descriptor?.defaultComparator ?? null;
    const editor = resolveEditor(descriptor, comparator);
    return {
        ...context.row,
        parameter: descriptor?.key ?? null,
        comparator,
        value: emptyValue(editor, comparator)
    };
}
export function buildRowForComparatorChange(context, descriptor, comparator) {
    const editor = resolveEditor(descriptor, comparator);
    const value = context.row.value?.type === expectedValueType(editor, comparator)
        ? context.row.value
        : emptyValue(editor, comparator);
    return {
        ...context.row,
        comparator,
        value
    };
}
export function buildRowWithValue(row, value) {
    return {
        ...row,
        value
    };
}
export function addFilterRow(groups, groupIndex, idFactory) {
    return groups.map((group, currentGroupIndex) => currentGroupIndex === groupIndex
        ? {
            ...group,
            rows: [...group.rows, createFilterRow(idFactory)]
        }
        : group);
}
export function removeFilterRow(groups, context) {
    const { group, groupIndex, rowIndex } = context;
    if (group.rows.length > 1) {
        return groups.map((currentGroup, currentGroupIndex) => currentGroupIndex === groupIndex
            ? {
                ...currentGroup,
                rows: currentGroup.rows.filter((_, currentRowIndex) => currentRowIndex !== rowIndex)
            }
            : currentGroup);
    }
    return groups.filter((_, currentGroupIndex) => currentGroupIndex !== groupIndex);
}
export function addFilterGroup(groups, idFactory) {
    return [...groups, createFilterGroup(idFactory)];
}
